import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { type Ref, useId, useImperativeHandle, useRef, useState } from 'react';
import { Checkbox } from '~/components/Checkbox';
import { richLink } from '~/components/Link';
import { visuallyHidden } from '~/components/Utilities';
import { getPathname } from '~/i18n/navigation';
import { trackEmailSubmitted, trackEvent } from '~/utils';
import styles from './EmailCaptureForm.module.css';
import { submitEmailCapture } from './submitEmailCapture';
const privacyLink = richLink('/legal/privacy-policy', {
  isInline: true
});
export type EmailCaptureFormProps = {
  ref?: Ref<EmailCaptureFormHandle>;
};
export type EmailCaptureFormHandle = {
  scrollIntoView: () => void;
  submitFromExternalCta: () => 'invalid' | 'valid';
  commitEmailCapture: (fullTripId: string) => void;
  getElement: () => HTMLElement | null;
};
type FormState = 'idle' | 'error-invalid' | 'submitting';
const EMAIL_REGEX = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{1,63}$/;
export function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}
export function EmailCaptureForm(t0) {
  const $ = _c(54);
  const {
    ref
  } = t0;
  const t = useTranslations();
  const locale = useLocale();
  const sectionRef = useRef(null);
  const inputRef = useRef(null);
  const submittingRef = useRef(false);
  const pendingDispatchRef = useRef(null);
  const [email, setEmail] = useState("");
  const [marketingOptOut, setMarketingOptOut] = useState(false);
  const [state, setState] = useState("idle");
  const prefersReducedMotion = _temp;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = behavior => {
      sectionRef.current?.scrollIntoView({
        behavior,
        block: "start"
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const scrollSelfIntoView = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setState("error-invalid");
      inputRef.current?.focus();
      scrollSelfIntoView(prefersReducedMotion() ? "auto" : "smooth");
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const flagAsInvalid = t2;
  let t3;
  if ($[2] !== email || $[3] !== marketingOptOut) {
    t3 = () => {
      const filled = inputRef.current?.value ?? email;
      if (filled !== email) {
        setEmail(filled);
      }
      const trimmed = filled.trim();
      if (trimmed === "") {
        pendingDispatchRef.current = null;
        return "valid";
      }
      if (!isValidEmail(trimmed)) {
        flagAsInvalid();
        return "invalid";
      }
      const marketingOptIn = !marketingOptOut;
      pendingDispatchRef.current = {
        email: trimmed,
        marketing_optin: marketingOptIn,
        newsletter_signup: marketingOptIn
      };
      if (submittingRef.current) {
        return "valid";
      }
      submittingRef.current = true;
      setState("submitting");
      trackEvent("playground_email_capture_submitted", {
        event_category: "Playground",
        event_label: "playground_email_capture_submitted",
        marketing_optin: marketingOptIn
      });
      return "valid";
    };
    $[2] = email;
    $[3] = marketingOptOut;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const runSubmit = t3;
  let t4;
  if ($[5] !== locale || $[6] !== runSubmit) {
    t4 = () => ({
      scrollIntoView: () => {
        scrollSelfIntoView("auto");
        inputRef.current?.focus({
          preventScroll: true
        });
      },
      getElement: () => sectionRef.current,
      submitFromExternalCta: runSubmit,
      commitEmailCapture: fullTripId => {
        const pending = pendingDispatchRef.current;
        if (pending === null) {
          return;
        }
        pendingDispatchRef.current = null;
        const origin = globalThis.window === undefined ? "" : globalThis.location.origin;
        const fullJourneyPath = `/journey/${fullTripId}`;
        const journeyUrl = `${origin}${getPathname({
          href: fullJourneyPath,
          locale
        })}?source=playground`;
        submitEmailCapture({
          email: pending.email,
          marketing_optin: pending.marketing_optin,
          newsletter_signup: pending.newsletter_signup,
          journey_url: journeyUrl,
          itinerary_id: fullTripId,
          locale
        }).then(result => {
          if (result.ok) {
            trackEmailSubmitted(pending.email, "playground_capture", {
              marketingOptOut: !pending.marketing_optin
            });
          }
        }).catch(_temp2);
      }
    });
    $[5] = locale;
    $[6] = runSubmit;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useImperativeHandle(ref, t4);
  let t5;
  if ($[8] !== state || $[9] !== t) {
    t5 = state === "error-invalid" ? t("playground.emailCapture.errorInvalid") : null;
    $[8] = state;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const errorMessage = t5;
  const emailInputId = useId();
  const marketingOptOutId = useId();
  const marketingCopyId = useId();
  let t6;
  if ($[11] !== t) {
    t6 = t("aria.email_capture_form");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("playground.emailCapture.heading");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <h2 className={styles.heading}>{t7}</h2>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t) {
    t9 = t("playground.emailCapture.placeholder");
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== emailInputId || $[20] !== t9) {
    t10 = <label htmlFor={emailInputId} className={visuallyHidden}>{t9}</label>;
    $[19] = emailInputId;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("playground.emailCapture.placeholder");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = state === "error-invalid";
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = event => setEmail(event.target.value);
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== email || $[26] !== emailInputId || $[27] !== t11 || $[28] !== t12) {
    t14 = <input ref={inputRef} id={emailInputId} type="email" className={styles.input} placeholder={t11} aria-invalid={t12} value={email} onChange={t13} />;
    $[25] = email;
    $[26] = emailInputId;
    $[27] = t11;
    $[28] = t12;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== errorMessage || $[31] !== state) {
    t15 = errorMessage !== null && <p key={state} role="alert" className={styles.error}>{errorMessage}</p>;
    $[30] = errorMessage;
    $[31] = state;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t) {
    t16 = t.rich("playground.emailCapture.marketingCopy", {
      privacy: privacyLink
    });
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== marketingCopyId || $[36] !== t16) {
    t17 = <p id={marketingCopyId} className={styles.privacy}>{t16}</p>;
    $[35] = marketingCopyId;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t) {
    t18 = t("playground.emailCapture.marketingOptOut");
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = event_0 => setMarketingOptOut(event_0.target.checked);
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== marketingCopyId || $[42] !== marketingOptOut || $[43] !== marketingOptOutId || $[44] !== t18) {
    t20 = <Checkbox id={marketingOptOutId} name="marketingOptOut" label={t18} checked={marketingOptOut} onChange={t19} aria-describedby={marketingCopyId} />;
    $[41] = marketingCopyId;
    $[42] = marketingOptOut;
    $[43] = marketingOptOutId;
    $[44] = t18;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t10 || $[47] !== t14 || $[48] !== t15 || $[49] !== t17 || $[50] !== t20 || $[51] !== t6 || $[52] !== t8) {
    t21 = <section ref={sectionRef} className={styles.form} aria-label={t6} data-test-id="email-capture-form">{t8}{t10}{t14}{t15}{t17}{t20}</section>;
    $[46] = t10;
    $[47] = t14;
    $[48] = t15;
    $[49] = t17;
    $[50] = t20;
    $[51] = t6;
    $[52] = t8;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  return t21;
}
function _temp2() {}
function _temp() {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
}